import React, { useContext, useEffect, useRef, useState } from "react";
import _ from "lodash";
import cx from "classnames";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import copy from "copy-to-clipboard";

import { useUserDetails } from "src/utils/collections.helper";

import Thumbnail from "src/components/thumbnail";
import Reveal from "src/components/animation";
import ContactCardViewToggle from "src/components/contactCardViewToggle";
import Tooltip from "src/components/tooltip";
import { BentoContext, BentoContextType } from "../layout";

import { CollectionAsset } from "src/types/collections";
import { CollectionContext } from "src/pages/collection/components/context";

import styles from "./styles.module.scss";
const DefaultThumbnail = '/images/default_user_profile_background.png';

interface ContactCardProps {
  asset: CollectionAsset;
  editable: boolean;
  // eslint-disable-next-line no-unused-vars
  onRemove: (asset: CollectionAsset) => void;
  // eslint-disable-next-line no-unused-vars
  initializeAnimation: () => void;
}

const ContactCard: React.FC<ContactCardProps> = ({
  asset,
  editable,
  onRemove,
  initializeAnimation = () => {},
}) => {
  const initialised = useRef(false);

  const { gridStack } = React.useContext<BentoContextType>(BentoContext);

  const [isHoveredOver, setIsHoveredOver] = useState(false);
  const [showContactNumberCopied, setShowContactNumberCopied] = useState(false);

  const { sectionId: currentSectionIndex } = useParams();

  const collectionContext = useContext(CollectionContext);

  const user = useSelector((state: any) => 
    _.get(state, "users.me", {})
  );
  const { userName, userTitle, userEmail, userImage, userContactNumber, linkedInUrl } = useUserDetails(user);

  const editCollection = useSelector((state: any) =>
    _.get(state, `${collectionContext.editSelector}`),
  );

  const assetViewType = _.get(asset, "metadata.contactCardLayout");
  const [selectedViewType, setSelectedViewType] = useState(assetViewType ?? "split");
  const [selectedWidth, setSelectedWidth] = useState(_.get(asset, "metadata.width", 1) || 1);
  const [selectedHeight, setSelectedHeight] = useState(_.get(asset, "metadata.height", 1) || 1);

  useEffect(() => {
    setSelectedViewType(assetViewType ?? "split");  
    setSelectedWidth(_.get(asset, "metadata.width", 1) || 1);
    setSelectedHeight(_.get(asset, "metadata.height", 1) || 1);
  }, [assetViewType, asset.metadata]);

  const assetRef = useRef(null);
  // Default to (3, 0) so the card is never placed under the description widget (0,0)-(2,1)
  const width = selectedWidth;
  const height = selectedHeight;
  const xaxis = _.get(asset, "metadata.xaxis", 3) ?? 3;
  const yaxis = _.get(asset, "metadata.yaxis", 0) ?? 0;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: asset.id,
    disabled: !editable,
    data: {
      asset: asset,
      type: "asset",
    },
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
  };

  useEffect(() => {
    if (gridStack?.current && assetRef.current) {
      const metadata = asset.metadata || {};
      const x = metadata.xaxis ?? 3;
      const y = metadata.yaxis ?? 0;
      const w = metadata.width ?? 1;
      const h = metadata.height ?? 1;

      if (!initialised.current) {
        gridStack.current.makeWidget(assetRef.current);
        initialised.current = true;
      } else {
        gridStack.current.update(assetRef.current, {
          x,
          y,
          w,
          h,
        });
      }
    }
  }, [assetRef.current, asset.metadata]);

  const handleRemove = () => {
    if (onRemove) {
      onRemove(asset);
    }
  };

  const handleViewSelect = (view: string) => {
    setSelectedViewType(view);
    const contactCard = currentSectionIndex
      ? _.find(
          _.get(
            editCollection,
            `sections[${currentSectionIndex ?? 0}].item.entities`,
            [],
          ),
          (entity) => _.get(entity, "type") === "CONTACT_CARD",
        )
      : _.find(
          editCollection.entities,
          (entity) => _.get(entity, "type") === "CONTACT_CARD",
        );

    collectionContext.updateAssetMetadata(
      currentSectionIndex ? Number(currentSectionIndex) : undefined,
      [
        {
          id: _.get(contactCard, "id") as string,
          metadata: {
            ..._.get(contactCard, "metadata", {}),
            contactCardLayout: view,
          },
        },
      ],
    );
  };

  const handleSizeSelect = (width: number, height: number) => {
    setSelectedWidth(width);
    setSelectedHeight(height);
    initializeAnimation();

    const contactCard = currentSectionIndex
      ? _.find(
          _.get(
            editCollection,
            `sections[${currentSectionIndex ?? 0}].item.entities`,
            [],
          ),
          (entity) => _.get(entity, "type") === "CONTACT_CARD",
        )
      : _.find(
          editCollection.entities,
          (entity) => _.get(entity, "type") === "CONTACT_CARD",
        );

    collectionContext.updateAssetMetadata(
      currentSectionIndex ? Number(currentSectionIndex) : undefined,
      [
        {
          id: _.get(contactCard, "id") as string,
          metadata: {
            ..._.get(contactCard, "metadata", {}),
            width,
            height,
          },
        },
      ],
    );
  };

  const handleContactNumberClick = () => {
    copy(userContactNumber);
    setShowContactNumberCopied(true);

    setTimeout(() => setShowContactNumberCopied(false), 5000);
  };

  const redirectLinkedInUrl = () => {
    if (linkedInUrl) {
      window.open(linkedInUrl, "_blank");
    }
  }

  return (
    <div
      className={cx(styles.cardContainer, "grid-stack-item", "contact-card", {
        [styles.editable]: editable,
      })}
      ref={assetRef}
      // eslint-disable-next-line react/no-unknown-property
      gs-id={asset.id}
      // eslint-disable-next-line react/no-unknown-property
      gs-w={width}
      // eslint-disable-next-line react/no-unknown-property
      gs-h={height}
      // eslint-disable-next-line react/no-unknown-property
      gs-x={xaxis}
      // eslint-disable-next-line react/no-unknown-property
      gs-y={yaxis}
      // eslint-disable-next-line react/no-unknown-property
      gs-no-resize="true"
      onMouseEnter={() => setIsHoveredOver(true)}
      onMouseLeave={() => setIsHoveredOver(false)}
    >
      <div
        className={cx(styles.inner, "grid-stack-item-content", {
          [styles.editable]: editable,
          [styles.dragging]: isDragging,
          [styles.full]: selectedViewType === "overlay",
          [styles.regularContactCard]: width === 2,
        })}
        style={style}
        ref={setNodeRef}
        {...listeners}
        {...attributes}
      >
        <div className={cx(styles.icon)}>
          <img
            className={styles.contactCardImage}
            style={{
              backgroundImage: `url('${userImage || DefaultThumbnail}')`,
            }}
          />
          <div className={styles.vignetteBlur}></div>
        </div>
        <Reveal show={editable}>
          <button className={styles.remove} onClick={handleRemove}>
            <i className="fa-regular fa-circle-xmark"></i>
          </button>
        </Reveal>
        <div className={styles.cardContent}>
          <div className={styles.nameAndTitle}>
            <div className={styles.nameContainer}>
              <Tooltip content={userName} placement="top">
                <p className={cx(styles.name, {
                  [styles.fullWidth]: !linkedInUrl || width !== 2,
                })}>
                  {userName}
                </p>
              </Tooltip>
              {width === 2 && linkedInUrl && <div
                className={styles.linkedIn}
                onClick={redirectLinkedInUrl}
              >
                <i className={cx(styles.icon, "paperflite-linkedin", styles.linkedInIcon)} />
              </div>}
            </div>
            {userTitle && <p className={styles.title}>
              {userTitle}
            </p>}
          </div>
          <div className={styles.ctaParentContainer}>
            {userEmail && <div className={styles.contentWrapper}>
              <div className={cx(styles.ctaContainer, styles.mail)}>
                <i className={cx(styles.icon, "paperflite-mail")} />
                <p className={styles.content}>
                  {userEmail}
                </p>
              </div>
            </div>}
            {userContactNumber && <div
              className={styles.ctaContainer}
              onClick={handleContactNumberClick}
            >
              <i className={cx(styles.icon, "paperflite-phone-call")} />
              <p
                className={cx(styles.content, {
                  [styles.showContactNumberCopied]: showContactNumberCopied,
                })}
              >
                {showContactNumberCopied ? "Copied!" : userContactNumber}
              </p>
            </div>}
            {linkedInUrl && width !== 2 && <div className={styles.contentWrapper} onClick={redirectLinkedInUrl}>
              <div className={cx(styles.ctaContainer)}>
                <i className={cx(styles.icon, "paperflite-linkedin")} />
              </div>
            </div>}
          </div>
        </div>
        <ContactCardViewToggle
          show={editable && isHoveredOver && !false}
          selectedView={selectedViewType!}
          views={[
            {
              type: "split",
              id: "split",
              icon: "paperflite-split",
            },
            {
              type: "overlay",
              id: "overlay",
              icon: "paperflite-overlay",
            },
          ]}
          onViewSelect={handleViewSelect}
          dimensions={[
            {
              id: "one",
              width: 1,
              height: 1,
            },
            {
              id: "two",
              width: 2,
              height: 1,
            },
          ]}
          width={selectedWidth}
          height={selectedHeight}
          onResize={handleSizeSelect}
        />
      </div>
    </div>
  );
};

const PlaceholderContactCard: React.FC<any> = () => {
  return (
    <div className={cx(styles.card, styles.placeholder)}>
      <div className={cx(styles.icon, styles.block)}></div>
    </div>
  );
};

const DraggingContactCard: React.FC<{
  asset: CollectionAsset;
  overSection: boolean;
}> = ({ asset, overSection }) => {
  const assetItem = asset.asset;

  return (
    <div
      className={cx(styles.cardContainer, styles.dragging, {
        [styles.overSection]: overSection,
      })}
    >
      <div className={styles.inner}>
        <div className={styles.icon}>
          <Thumbnail media={assetItem?.icon} defaultImage={DefaultThumbnail} />
        </div>
      </div>
    </div>
  );
};

export {
  ContactCard,
  type ContactCardProps,
  PlaceholderContactCard,
  DraggingContactCard,
};
